import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import Dashboard from "./pages/Dashboard";
import RequireAuth from "./auth/RequireAuth";
import RoleGuard from "./auth/RoleGuard";
import Unauthorized from "./pages/Unauthorized";

import Onboarding from "./pages/Onboarding";
import Help from "./pages/Help";
import AttStatusCheck from "./pages/AttStatusCheck";
import ReportingDashboard from "./pages/ReportingDashboard";

import ManageRequests from "./pages/ManageRequest";
import RequestDetail from "./pages/RequestDetail";
import ApprovalsList from "./pages/ApprovalsList";
import ApprovalDetail from "./pages/ApprovalDetail";

import OpsExceptions from "./pages/OpsExceptions";
import OpsRetryEditor from "./pages/OpsRetryEditor";

import AuthCallback from "./auth/AuthCallback";
import RootLanding from "./RootLanding";

/**
 * Role access rules (per latest clarification):
 * - Manage Requests: Nominator, FTE Nominator, DTV OPS Admin
 * - Approvals: Employee Sponsor, Employee Supplier Admin, DTV OPS Admin, CP Channel Delegate
 * - Onboarding (new): Nominator, FTE Nominator
 * - Onboarding (edit by id): Nominator, FTE Nominator, Employee Sponsor
 *
 * So: Employee Sponsor can ONLY access /onboarding/new/:id (not /onboarding/new).
 * Nominator + FTE Nominator can access BOTH /onboarding/new and /onboarding/new/:id.
 *
 * Note: OPS tools remain restricted to DTV OPS Admin + Employee Supplier Admin.
 */
const OPS_GROUP = ["DTV OPS Admin", "Employee Supplier Admin"];

const MANAGE_REQUESTS_GROUP = ["Nominator", "FTE Nominator", "DTV OPS Admin"];

const APPROVALS_GROUP = [
    "Employee Sponsor",
    "Employee Supplier Admin",
    "DTV OPS Admin",
    "CP Channel Delegate",
];

const ONBOARDING_NEW_GROUP = ["DTV OPS Admin", "Employee Supplier Admin", "Nominator", "FTE Nominator"];
const ONBOARDING_EDIT_BY_ID_GROUP = ["DTV OPS Admin", "Employee Supplier Admin", "Nominator", "FTE Nominator", "Employee Sponsor"];

/**
 * Help page is available to all authenticated TeamBridge roles
 * so users can access process guidance in one place.
 */
const HELP_GROUP = [
    "DTV OPS Admin",
    "Employee Supplier Admin",
    "Nominator",
    "FTE Nominator",
    "Employee Sponsor",
    "CP Channel Delegate",
    "Read Only",
];

/**
 * AT&T Status Check is available to all authenticated TeamBridge roles.
 */
const ATT_STATUS_CHECK_GROUP = [
    "DTV OPS Admin",
    "Employee Supplier Admin",
    "Nominator",
    "FTE Nominator",
    "Employee Sponsor",
    "CP Channel Delegate",
    "Read Only",
];


/**
 * Reporting Dashboard (read-only Hinge RPT data) is available to all
 * authenticated TeamBridge roles, including Read Only.
 */
const REPORTING_GROUP = [
    "DTV OPS Admin",
    "Employee Supplier Admin",
    "Nominator",
    "FTE Nominator",
    "Employee Sponsor",
    "CP Channel Delegate",
    "Read Only",
];

function OnboardingNewGate() {
    const loc = useLocation();

    // Reactive role state — polls sessionStorage so this component re-evaluates
    // when the user switches roles via RoleSwitcher (same-tab, no full reload).
    const [role, setRole] = useState(() => sessionStorage.getItem("tb_role") || "");
    useEffect(() => {
        const sync = () => {
            const next = sessionStorage.getItem("tb_role") || "";
            setRole((prev) => (prev !== next ? next : prev));
        };
        sync();
        const poll = setInterval(sync, 200);
        const onStorage = (e) => {
            if (e.storageArea === sessionStorage && e.key === "tb_role") sync();
        };
        window.addEventListener("storage", onStorage);
        return () => {
            clearInterval(poll);
            window.removeEventListener("storage", onStorage);
        };
    }, []);

    const roleLc = String(role).trim().toLowerCase();
    const isFteNominator = roleLc === "fte nominator";
    const isBareOnboardingNew = String(loc?.pathname || "") === "/onboarding/new";

    let hasIdentifierQuery = false;
    try {
        const usp = new URLSearchParams(loc?.search || "");
        const idLike =
            String(usp.get("dtvid") || "").trim() ||
            String(usp.get("dtvId") || "").trim() ||
            String(usp.get("DTVID") || "").trim() ||
            String(usp.get("npwid") || "").trim() ||
            String(usp.get("npwId") || "").trim() ||
            String(usp.get("npw_id") || "").trim();
        hasIdentifierQuery = !!idLike;
    } catch {
        hasIdentifierQuery = false;
    }

    if (isFteNominator && isBareOnboardingNew && !hasIdentifierQuery) {
        const from = `${loc?.pathname || ""}${loc?.search || ""}` || "/onboarding/new";
        return (
            <Unauthorized
                from={from}
                allowed={["Nominator", "DTV OPS Admin", "Employee Supplier Admin"]}
                role={role}
            />
        );
    }

    return <Onboarding />;
}

/**
 * Returns the current tb_role from sessionStorage, updated reactively via poll.
 * Used as a `key` prop on role-sensitive pages so they remount when role changes,
 * ensuring all internal sessionStorage reads inside those pages pick up the new value.
 */
function useRoleKey() {
    const [roleKey, setRoleKey] = useState(() => sessionStorage.getItem("tb_role") || "");
    useEffect(() => {
        const sync = () => {
            const next = sessionStorage.getItem("tb_role") || "";
            setRoleKey((prev) => (prev !== next ? next : prev));
        };
        const poll = setInterval(sync, 200);
        const onStorage = (e) => {
            if (e.storageArea === sessionStorage && e.key === "tb_role") sync();
        };
        window.addEventListener("storage", onStorage);
        return () => {
            clearInterval(poll);
            window.removeEventListener("storage", onStorage);
        };
    }, []);
    return roleKey;
}

export default function App() {
    const roleKey = useRoleKey();
    return (
        <Routes>
            <Route path="/unauthorized" element={<Unauthorized />} />

            {/* Protect root-like entries so login can start and deep-link restore can run */}
            <Route
                path="/"
                element={
                    <RequireAuth>
                        <RootLanding />
                    </RequireAuth>
                }
            />
            <Route
                path="/index.html"
                element={
                    <RequireAuth>
                        <RootLanding />
                    </RequireAuth>
                }
            />
            <Route
                path="/index.js"
                element={
                    <RequireAuth>
                        <RootLanding />
                    </RequireAuth>
                }
            />

            {/* Present but harmless even when redirectUri="origin" */}
            <Route path="/auth/callback" element={<AuthCallback />} />

            <Route
                path="/dashboard"
                element={
                    <RequireAuth>
                        {/* key={roleKey} forces Dashboard to fully remount when role changes
                            so all internal sessionStorage reads pick up the new role value */}
                        <Dashboard key={roleKey} />
                    </RequireAuth>
                }
            />


            {/* AT&T Status Check */}
            <Route
                path="/att-status-check"
                element={
                    <RequireAuth>
                        <RoleGuard allowed={ATT_STATUS_CHECK_GROUP}>
                            <AttStatusCheck />
                        </RoleGuard>
                    </RequireAuth>
                }
            />


            {/* Reporting Dashboard (Hinge RPT) */}
            <Route
                path="/reporting"
                element={
                    <RequireAuth>
                        <RoleGuard allowed={REPORTING_GROUP}>
                            <ReportingDashboard key={roleKey} />
                        </RoleGuard>
                    </RequireAuth>
                }
            />

            {/* Help */}
            <Route
                path="/help"
                element={
                    <RequireAuth>
                        <RoleGuard allowed={HELP_GROUP}>
                            <Help />
                        </RoleGuard>
                    </RequireAuth>
                }
            />

            {/* Onboarding */}
            <Route
                path="/onboarding/new"
                element={
                    <RequireAuth>
                        <RoleGuard allowed={ONBOARDING_NEW_GROUP}>
                            <OnboardingNewGate />
                        </RoleGuard>
                    </RequireAuth>
                }
            />
            <Route
                path="/onboarding/new/:id"
                element={
                    <RequireAuth>
                        <RoleGuard allowed={ONBOARDING_EDIT_BY_ID_GROUP}>
                            <Onboarding />
                        </RoleGuard>
                    </RequireAuth>
                }
            />

            {/* Manage Requests */}
            <Route
                path="/manage-requests"
                element={
                    <RequireAuth>
                        <RoleGuard allowed={MANAGE_REQUESTS_GROUP}>
                            <ManageRequests key={roleKey} />
                        </RoleGuard>
                    </RequireAuth>
                }
            />
            <Route
                path="/manage-requests/:id"
                element={
                    <RequireAuth>
                        <RoleGuard allowed={MANAGE_REQUESTS_GROUP}>
                            <RequestDetail />
                        </RoleGuard>
                    </RequireAuth>
                }
            />

            {/* Approvals */}
            <Route
                path="/approvals"
                element={
                    <RequireAuth>
                        <RoleGuard allowed={APPROVALS_GROUP}>
                            <ApprovalsList key={roleKey} />
                        </RoleGuard>
                    </RequireAuth>
                }
            />
            <Route
                path="/approvals/:id"
                element={
                    <RequireAuth>
                        <RoleGuard allowed={APPROVALS_GROUP}>
                            <ApprovalDetail />
                        </RoleGuard>
                    </RequireAuth>
                }
            />

            {/* OPS */}
            <Route
                path="/ops/exceptions"
                element={
                    <RequireAuth>
                        <RoleGuard allowed={OPS_GROUP}>
                            <OpsExceptions />
                        </RoleGuard>
                    </RequireAuth>
                }
            />

            <Route
                path="/ops/retry"
                element={
                    <RequireAuth>
                        <RoleGuard allowed={OPS_GROUP}>
                            <OpsRetryEditor />
                        </RoleGuard>
                    </RequireAuth>
                }
            />

            {/* Fallback to neutral root, NOT /dashboard */}
            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
    );
}
